import axios from 'axios'
import { getToken, refreshToken, getUid } from '@/utils/auth'
import { DEV_MODE } from '@/config/dev-mode'
import store from '@/store'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { throttle } from 'lodash'
import {
  applyAuthUserIdHeader,
  normalizeAuthUserId,
  writeAuthUserId,
} from '@/utils/authUserId'

let xidPromise = null
export const getXID = () => {
  if (!xidPromise) {
    xidPromise = (async () => {
      let xid = localStorage.getItem('xid')
      if (!xid || xid.trim() === '') {
        const fp = await FingerprintJS.load()
        const result = await fp.get()
        xid = result.visitorId
        localStorage.setItem('xid', xid)
      }
      return xid
    })()
  }
  return xidPromise
}

const isWebView = () => !!window.ReactNativeWebView

export const getClientType = () => {
  if (isWebView()) return 'APP'
  const isMobile = /Mobi|Android|iPhone/i.test(navigator.userAgent)
  return isMobile ? 'MOBILE_WEB' : 'PC_WEB'
}

export function setupInterceptors(router) {
  // 토큰 갱신 중 다른 요청들을 대기시키기 위한 큐
  let isRefreshing = false
  let failedQueue = []

  const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject, config }) => {
      if (error) {
        reject(error)
      } else {
        // 새 토큰으로 헤더 업데이트 후 재요청
        config.headers.Authorization = `Bearer ${token}`
        applyAuthUserIdHeader(config.headers, getUid())
        resolve(axios.request(config))
      }
    })
    failedQueue = []
  }

  const refresh = async (config) => {
    // 다른 요청이 이미 갱신해 둔 토큰이 있으면 그것으로 재요청
    // (exp만 보면 서버가 revoke한 토큰을 그대로 재사용해 무한 루프가 발생함)
    const currentToken = getToken()
    const usedToken = config.headers.Authorization?.replace(/^Bearer\s+/, '')
    if (currentToken && currentToken !== usedToken) {
      config.headers.Authorization = `Bearer ${currentToken}`
      applyAuthUserIdHeader(config.headers, getUid())
      return axios.request(config)
    }

    // 이미 refresh 진행 중이면 큐에 추가하고 대기
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config })
      })
    }

    isRefreshing = true

    try {
      const data = await refreshToken()
      sessionStorage.setItem('userInfo', JSON.stringify(data))
      sessionStorage.setItem('X-Token', data.access_token)
      sessionStorage.setItem('refresh_token', data.refresh_token)
      const refreshedUid = writeAuthUserId(normalizeAuthUserId(data.id) ?? getUid())

      // 대기 중인 요청들 일괄 처리
      processQueue(null, data.access_token)

      config.headers.Authorization = `Bearer ${data.access_token}`
      applyAuthUserIdHeader(config.headers, refreshedUid)
      return axios.request(config)
    } catch (error) {
      // 대기 중인 요청들 모두 실패 처리
      processQueue(error, null)
      handleErrorResponse(error)
      return Promise.reject(error)
    } finally {
      isRefreshing = false
    }
  }

  // ✅ 웹뷰인 경우 앱에게 알림만 보냄
  const postNoTokenToWebView = throttle(() => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ action: 'noToken' })
    )
  }, 3000, { leading: true, trailing: false })

  // 서버에서 토큰이 무효화된 경우(탈퇴/관리자 강제 로그아웃 등) 앱에 로그아웃 요청
  const postLogoutToWebView = throttle(() => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ action: 'logout' })
    )
  }, 3000, { leading: true, trailing: false })

  const isFatalAuthMessage = (data) => {
    const detail = data?.detail || ''
    const desc = data?.error_description || ''
    return (
      detail.startsWith('Access token revoked') ||
      detail.startsWith('Refresh token') ||
      detail === 'Could not validate credentials' ||
      detail === 'User not found' ||
      desc.startsWith('Invalid refresh token')
    )
  }

  const requestInterceptorId = axios.interceptors.request.use(
    async (config) => {
      const xIdentifier = await getXID().then((xid) => xid)
      const token = getToken()
      const uid = getUid()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
        applyAuthUserIdHeader(config.headers, uid)
      } else {
        const guestInfoId = sessionStorage.getItem('guestInfoId')
        applyAuthUserIdHeader(config.headers, guestInfoId)
      }

      config.headers['X-Identifier'] = xIdentifier
      config.headers['X-Client-Type'] = getClientType()
      return config
    },
    (error) => Promise.reject(error),
  )

  const responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response && error.response.status === 401) {
        const isInWebView = isWebView()
        DEV_MODE && console.log(error.response.data)

        if (isInWebView) {
          // 토큰이 서버에서 무효화된 경우 앱에 로그아웃 요청, 단순 만료면 noToken으로 갱신 유도
          if (isFatalAuthMessage(error.response.data)) {
            postLogoutToWebView()
          } else {
            postNoTokenToWebView()
          }
          return Promise.resolve({ data: null })
        }

        const detail = error.response.data?.detail
        const isAuthRetryable =
          detail?.startsWith('Access token expired') ||
          detail?.startsWith('Access token revoked')

        // 같은 요청에 대해 1회만 재시도하도록 가드 (무한 루프 방지 안전망)
        if (isAuthRetryable && !error.config._retry) {
          error.config._retry = true
          return refresh(error.config)
        }

        handleErrorResponse(error)
        return Promise.reject(error)
      }
      handleErrorResponse(error)
      return Promise.reject(error)
    },
  )

  // 중복 경고 방지 플래그
  let isHandlingAuthError = false

  function handleErrorResponse(error) {
    if (error.response?.status === 401 && isFatalAuthMessage(error.response.data)) {
      // 이미 처리 중이면 중복 실행 방지
      if (isHandlingAuthError) return
      isHandlingAuthError = true

      // Vuex user state까지 비워야 헤더 등 UI가 로그인 상태로 남지 않음
      // (logout 액션이 sessionStorage 정리와 clearAllUserCaches까지 함께 수행)
      store.dispatch('user/logout')
      alert('로그인 세션이 종료되었습니다. 다시 로그인해주세요.')

      // 현재 경로를 to 쿼리로 전달하여 로그인 후 돌아올 수 있게 함
      const currentPath = window.location.pathname + window.location.search
      const loginPath = `/auth/login?to=${encodeURIComponent(currentPath)}`

      // router가 초기화되지 않은 경우 location으로 대체
      if (router?.push) {
        router.push(loginPath)
      } else {
        window.location.href = loginPath
      }

      // 페이지 전환 후 플래그 리셋 (다음 세션을 위해)
      setTimeout(() => {
        isHandlingAuthError = false
      }, 1000)
      return
    }
    // 추가적인 에러 처리 로직

    //router.push('/error/error01')
  }

  return () => {
    axios.interceptors.request.eject(requestInterceptorId)
    axios.interceptors.response.eject(responseInterceptorId)
  }
}

export default setupInterceptors
